import { z } from 'zod';
/**
 * Background jobs and the queues they run on.
 *
 * Two things make this screen different from every other list in the console.
 *
 * **It does not page on a cursor.** BullMQ's job lists are index ranges over
 * Redis structures with no stable sort key to seek on, and a live queue shifts
 * under you as workers drain it. An opaque cursor here would be an offset
 * wearing a disguise, and would claim a guarantee — "every row exactly once" —
 * that cannot hold for a queue being worked. So it pages on an explicit offset
 * and says so.
 *
 * **Job payloads are redacted.** A `food-photo-analysis` job carries the user's
 * meal photograph as base64. Nothing in the console needs to see it, and the
 * health-data rule (design spec §5) does not stop applying because the data
 * arrived via Redis.
 */
export const QUEUE_NAMES = [
    'plan-generation',
    'food-photo-analysis',
    'notifications',
    'weekly-replan',
    'memory-extraction',
    'weekly-review',
    'monthly-recap',
    'day-reads',
    // Steado for Gyms: published trainer weeks becoming the member's plan.
    'gym-plan-activation',
    'gym-plan-draft',
    // Steado for Gyms: the 09:00 sweep telling trainers which reviews fell due.
    'gym-push-review',
    // The one scanner: routing a photo, and the equipment scan itself.
    'scan-intake',
    // The console's AI draft of a catalog exercise for an exercise request.
    'exercise-draft',
    // The AI research of a food request a meal, the coach or a photo filed.
    'food-request-research',
    // The quarter-hourly sweep keeping each catalog food's profile embedding current.
    'food-embeddings',
    // The cooking guides of a freshly active plan week, written ahead of the first recipe request.
    'cooking-guides',
];
/**
 * The states worth looking at. BullMQ also has `waiting-children` and
 * `prioritized`; neither is reachable with the job options this API uses, and
 * offering a filter that always returns nothing is worse than omitting it.
 */
export const JOB_STATES = ['waiting', 'active', 'delayed', 'failed', 'completed'];
export const queueCountsSchema = z.object({
    waiting: z.number().int(),
    active: z.number().int(),
    delayed: z.number().int(),
    failed: z.number().int(),
    completed: z.number().int(),
});
export const adminQueueSchema = z.object({
    name: z.enum(QUEUE_NAMES),
    isPaused: z.boolean(),
    counts: queueCountsSchema,
});
/**
 * A job as the console shows it.
 *
 * `data` is the redacted payload — see `redactJobPayload` on the API side. The
 * shape is `unknown` because it differs per queue and Redis enforces nothing;
 * pretending otherwise in a shared schema would mean inventing a union.
 */
export const adminJobSchema = z.object({
    id: z.string(),
    name: z.string(),
    state: z.enum(JOB_STATES),
    data: z.unknown(),
    attemptsMade: z.number().int(),
    /** From the job's own options — how many attempts it is allowed in total. */
    maxAttempts: z.number().int(),
    /** Null until it fails. Truncated; a failure message is not a log sink. */
    failedReason: z.string().nullable(),
    /** Most recent stack first. Empty until it fails. */
    stacktrace: z.array(z.string()),
    createdAt: z.string(),
    processedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    /** Milliseconds this job is held before becoming runnable. */
    delayMs: z.number().int(),
});
export const adminQueueParamsSchema = z.object({
    queue: z.enum(QUEUE_NAMES),
});
export const adminJobParamsSchema = adminQueueParamsSchema.extend({
    /** BullMQ job ids are strings, not uuids — usually a Redis counter. */
    jobId: z.string().min(1).max(64),
});
export const JOBS_MAX_PAGE = 100;
export const adminJobListQuerySchema = z.object({
    state: z.enum(JOB_STATES).default('failed'),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(JOBS_MAX_PAGE).default(20),
});
/**
 * Offset paging, deliberately not the cursor meta every other list uses. The
 * shape differs because the guarantee differs, and a matching envelope over a
 * weaker promise is how a caller ends up trusting the wrong thing.
 */
export const offsetPaginationMetaSchema = z.object({
    offset: z.number().int(),
    limit: z.number().int(),
    hasMore: z.boolean(),
    /** The queue's count for this state when the page was read. Live, so it moves. */
    total: z.number().int(),
});
//# sourceMappingURL=jobs.js.map